import React from 'react';
import { ZoomIn } from 'react-bootstrap-icons';
import { getImageSrc } from '../../utils/imageSrc.js';
import { formatMoney, formatNomenclatureName, formatNumber, formatProductionName } from '../../utils/gazTextFormat.js';
import { palletsWord } from '../../utils/gazDeclensions.js';
import { GAZ_UNLOAD_SERVICE_KEYS, buildGazProposalTotals } from './gazProposalTotals.js';
import { getGazPricePerM3 } from '../Productions/gazComparisonView.js';
import { belorusskiyZavod, BELARUS_PREDUPREZHDENIE } from '../../utils/belorusskieZavody.js';
import { buildGazSummaryMetrics } from './gazSummaryMetrics.js';
import { buildVtoroySortHintManager, isVtoroySortName } from './gazVtoroySort.js';
import './gazProposal.css';

// Верхняя сводка расчёта для МСК Газобетона: что продаём, сколько к оплате и из чего
// сумма складывается. Своей арифметики по деньгам нет — складываются уже посчитанные
// суммы строк блоков.

// Деление сумм по разделам вынесено в gazProposalTotals.js: оно проверяется
// исполнением, а не сравнением строк исходника. Здесь только показ.
export { GAZ_UNLOAD_SERVICE_KEYS, buildGazProposalTotals };

// TODO: временный хардкод. Заменить на динамического ответственного логиста,
// когда сотрудник будет приходить в расчёт вместе со сделкой; пока имя и ссылка
// на карточку в CRM живут одной константой, чтобы правка была в одном месте.
const GAZ_RESPONSIBLE_LOGIST = {
  name: 'Сергей Степанов',
  url: 'https://td.monolit-crm.ru/company/personal/user/2069/',
};

/**
 * @param {object}  props
 * @param {object}  props.totals       — результат buildGazProposalTotals
 * @param {object}  props.vehicleModel — модель загрузки машин
 * @param {string}  props.totalLabel   — «К оплате» / «Итого»
 * @param {string}  props.totalValue   — итоговая сумма строкой (берётся из КП)
 * @param {object}  [props.fullTruckBasis] — материалы с доставкой и объём варианта,
 *                                           который везёт шаланда, для цены за куб
 * @param {number}  [props.displayVolumeM3] — объём показанного варианта, для цены
 *                                            за куб самих материалов
 * @param {object}  [props.headline]   — производство и его точная номенклатура
 * @param {object}  [props.facts]      — что просил клиент и что фактически отгрузим
 * @param {object}  [props.metrics]     — материалы/доставка/разгрузка, уже разложенные
 * @param {object}  [props.cheapestHint] — есть ли вариант дешевле выбранного
 * @param {object}  [props.brandHint]   — завод, который искали в «Товарах»
 * @param {boolean} [props.hasUpsells]  — в заказе есть добавленные допродажи
 * @param {object}  [props.photo]       — фото завода: { available, urls, onOpen }
 * @param {function}[props.onShowComparison] — переход к таблице сравнения
 * @param {function}[props.onSelectProduction] — посчитать по другому производству
 */
const GazProposalSummary = ({
  totals,
  metrics,
  vehicleModel,
  totalLabel,
  totalValue,
  fullTruckBasis,
  displayVolumeM3,
  headline,
  facts,
  cheapestHint,
  brandHint,
  hasUpsells = false,
  photo = null,
  deliveryDistanceKm = null,
  onShowComparison,
  onSelectProduction,
}) => {
  if (!totals) {
    return null;
  }

  // Километраж показываем только когда он действительно пришёл из расчёта.
  // Ноль — это «везти некуда», а не «неизвестно»: подписи в таком случае нет.
  const rasstoyanie = Number(deliveryDistanceKm);
  const deliveryKm = Number.isFinite(rasstoyanie) && rasstoyanie > 0
    ? Math.round(rasstoyanie)
    : null;


  const production = headline?.production ? formatProductionName(headline.production) : null;
  const items = Array.isArray(headline?.items) ? headline.items : [];
  // - Второй сорт узнаётся по имени позиции, которая реально поедет (12.10.2026, Роман).
  //   Условия продажи у него другие, и менеджер обязан проговорить их до отгрузки:
  //   блок невозвратный, оплата 100%.
  const estVtoroySort = items.some((item) => isVtoroySortName(item?.name ?? item));

  // Фактическая кубатура — в той же строке названия. Значение берётся готовым из
  // фактов: у каждой позиции своя, из того же состава груза, что даёт общий итог
  // «Фактически отгрузим кратно поддонам». Своего расчёта здесь нет.
  //
  // Единственная позиция берёт общий объём: он и есть её объём, и строка не
  // теряет кубатуру, даже если имя в составе груза записано иначе.
  const volumeOf = (name) => {
    const own = facts?.volumeByName?.[name];
    const value = Number.isFinite(Number(own)) ? Number(own) : null;
    if (value === null && items.length === 1) {
      return Number(facts?.factVolume);
    }
    return value;
  };

  // Отсутствует или невалидно — разделителя и пустого значения тоже нет.
  const withFactVolume = (name) => {
    const value = volumeOf(name);
    const text = value > 0 ? formatNumber(value) : null;
    return `${formatNomenclatureName(name)}${text ? ` · ${text} м³` : ''}`;
  };

  // Цена за куб при погрузке шаланды — той же функцией, что и в таблице сравнения.
  // Основание приходит готовым из варианта, который везёт шаланда: материалы с
  // доставкой без разгрузки и объём, который этот вариант действительно везёт.
  // Поэтому показатель не меняется при переключении вариантов заказа, а своей
  // формулы здесь по-прежнему нет.
  const pricePerM3 = getGazPricePerM3({
    payableTotal: fullTruckBasis?.payableTotal,
    volumeM3: fullTruckBasis?.volumeM3,
  });

  // Раскладка на материалы, доставку и разгрузку приходит готовой: разгрузка
  // показывается отдельно даже тогда, когда в самом КП она зашита в строку рейса
  // манипулятора. Запасной путь — по строкам блоков, как раньше.
  const view = metrics || buildGazSummaryMetrics({ totals });
  const unloadingTotal = view.unloadingTotal;
  const otherServiceRows = view.otherServiceRows;

  // Цена самого куба: стоимость материалов показанного варианта, делённая на его же
  // объём. Это ровно та величина, которая, умноженная на объём, и даёт «Материалы»
  // слева. Доставки и разгрузки в ней нет, делит та же функция.
  const materialsPricePerM3 = getGazPricePerM3({
    payableTotal: view.materialsTotal,
    volumeM3: displayVolumeM3,
  });

  // Два быстрых выбора производства. Своих цифр ни у одного нет: название, сумма
  // и разница берутся из готовых записей сравнения.

  // Выгодный вариант. Когда он уже выбран, кнопки нет — переключать некуда.
  const cheapestPick = cheapestHint && !cheapestHint.isCheapest && onSelectProduction
    ? cheapestHint.cheapest
    : null;

  // Требуемый завод — тот, что менеджер искал в «Товарах»; подсказка приносит его
  // строку сравнения с итогом и разницей. Дубля не будет: сама подсказка молчит,
  // когда искомый завод уже выбран или его нет в сравнении, а здесь снимается ещё
  // и совпадение с выгодным вариантом — за него отвечает первая кнопка.
  const requestedPick = brandHint && onSelectProduction
    && brandHint.best?.comparisonId !== cheapestPick?.comparisonId
    ? brandHint
    : null;

  return (
    <section className="gaz-sum" aria-label="Итог расчёта">
      {/* Кто поставляет — коротким бейджем, что именно — списком под ним. Одна
          позиция не становится «списком из одного»: маркер лишний, когда считать
          нечего. Порядок чтения: поставщик → состав → сумма — тот же, в каком
          менеджер объясняет заказ клиенту. */}
      {(production || items.length > 0) && (
        <header className="gaz-sum__what">
          {/* Миниатюра фото — слева от названия. Текстовая ссылка на её месте
              читалась как подпись, и её не замечали: картинка сама говорит, что
              смотреть есть что. Нет фото — нет и миниатюры, тексты тогда стоят
              ровно так же, как стояли до неё. */}
          {photo?.available && photo?.urls?.length > 0 && (
            <button
              type="button"
              className="gaz-sum__thumb"
              onClick={photo.onOpen}
              aria-label={`Открыть фото: ${production || 'газобетон'}`}
              title="Открыть фото"
            >
              <img className="gaz-sum__thumb-image" src={getImageSrc(photo.urls[0])} alt="" />
              <span className="gaz-sum__thumb-zoom" aria-hidden="true"><ZoomIn size={16} /></span>
              {/* Сколько всего фото — чтобы менеджер знал, что за первым есть ещё.
                  При одном снимке счётчик не нужен: он сообщал бы «их один». */}
              {photo.urls.length > 1 && (
                <span className="gaz-sum__thumb-count">{photo.urls.length}</span>
              )}
            </button>
          )}
          <div className="gaz-sum__texts">
          {production && <span className="gaz-sum__production">{production}</span>}
          {items.length > 1 ? (
            <ul className="gaz-sum__items">
              {items.map((name) => (
                <li className="gaz-sum__item" key={name}>{withFactVolume(name)}</li>
              ))}
            </ul>
          ) : items.length === 1 ? (
            <p className="gaz-sum__item gaz-sum__item--single">{withFactVolume(items[0])}</p>
          ) : null}
          {/* Ответственный логист — внутри той же шапки, а не отдельной карточкой:
              строка вторичная и не спорит ни с названием товара, ни с суммой.
              Своего условия показа у неё нет: сводка рисуется только внутри
              области и только по готовому расчёту, значит и ссылка живёт ровно
              столько же. */}
          <p className="gaz-sum__manager">
            Ответственный логист:{' '}
            <a
              className="gaz-sum__manager-link"
              href={GAZ_RESPONSIBLE_LOGIST.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {GAZ_RESPONSIBLE_LOGIST.name}
            </a>
          </p>
          </div>
        </header>
      )}

      {/* Срок оформления машин из Беларуси — тот же текст, что над таблицей
          сравнения. Стоит после шапки: внутри неё блок попадал в текстовую
          колонку рядом с миниатюрой, и левый край уезжал (Роман, 29.10.2026). */}
      {belorusskiyZavod(production) && (
        <p className="gaz-sum__belarus">{BELARUS_PREDUPREZHDENIE}</p>
      )}

      <div className="gaz-sum__total">
        <span className="gaz-sum__total-label">{totalLabel}</span>
        <span className="gaz-sum__total-value">{totalValue}</span>
      </div>

      {/* Четыре величины, по которым менеджер ведёт разговор: за что платят, во
          что обходится куб, сколько стоит привезти и сколько — разгрузить.
          Своей арифметики здесь нет: суммы приходят готовыми из блоков КП, цена
          за куб считается той же функцией, что и в таблице сравнения. */}
      <dl className="gaz-sum__metrics">
        <div>
          <dt>Материалы</dt>
          <dd>{formatMoney(view.materialsTotal)} ₽</dd>
        </div>
        <div>
          <dt>Цена за м³</dt>
          <dd>{materialsPricePerM3 ? `${formatMoney(materialsPricePerM3)} ₽` : '—'}</dd>
        </div>
        <div>
          <dt>Цена за м³ с доставкой при погрузке полной шаланды</dt>
          <dd>{pricePerM3 ? `${formatMoney(pricePerM3)} ₽` : '—'}</dd>
        </div>
        <div>
          {/* Километраж рядом с суммой: менеджер сразу видит, далеко ли везти,
              не открывая подсказку (Роман, 20.10.2026). Берётся готовым из
              расчёта доставки — своего пересчёта здесь нет. */}
          <dt>Доставка{deliveryKm !== null ? ` (${formatNumber(deliveryKm)} км)` : ''}</dt>
          <dd>{view.deliveryTotal > 0 ? `${formatMoney(view.deliveryTotal)} ₽` : '—'}</dd>
        </div>
        <div>
          <dt>Разгрузка</dt>
          <dd>{unloadingTotal > 0 ? `${formatMoney(unloadingTotal)} ₽` : '—'}</dd>
        </div>
      </dl>

      {otherServiceRows.length > 0 && (
        <p className="gaz-sum__services">
          {otherServiceRows.map((row) => `${row.name || 'Услуги'}: ${formatMoney(row.sum)} ₽`).join(' · ')}
        </p>
      )}

      {/* Сколько просил клиент и сколько уедет на самом деле. Отдельной строки об
          округлении нет: подпись сама говорит, что отгружаем кратно поддонам, а
          разницу видно из двух чисел рядом. */}
      {facts && facts.requestedVolume && (
        <dl className="gaz-sum__facts">
          <div>
            <dt>Нужно клиенту</dt>
            <dd>{formatNumber(facts.requestedVolume)} м³</dd>
          </div>
          <div>
            <dt>Фактически отгрузим кратно поддонам</dt>
            <dd>
              {[
                `${formatNumber(facts.factVolume)} м³`,
                facts.pallets ? `${formatNumber(facts.pallets)} ${palletsWord(facts.pallets)}` : null,
                facts.pieces ? `${formatNumber(facts.pieces)} блоков` : null,
              ].filter(Boolean).join(' · ')}
            </dd>
          </div>
        </dl>
      )}

      {/* Условия второго сорта. Менеджер обязан проговорить их до отгрузки:
          блок невозвратный, оплата 100%. Плашка появляется сама у любой позиции
          со словами «2 сорт» в названии — включать её вручную негде и незачем. */}
      {estVtoroySort && (
        <p className="gaz-sum__vtoroy-sort" role="note">
          {buildVtoroySortHintManager(items.map((item) => ({ name: item?.name ?? item })))}
        </p>
      )}

      {/* Предупреждение о более дешёвом производстве по уже посчитанной таблице
          сравнения. Само оно ничего не переключает: производство меняется только
          по нажатию на строку выбора, и тем же обработчиком, что и клик по
          строке сравнения. */}
      {/* Сравнение производств строится только по строкам «Товаров»: добавленные
          допродажи в него не попадают, а подставлять чужой U-блок другому заводу
          нельзя. Поэтому подпись прямо говорит, что именно сравнивается, и сумма
          выбранного производства не выдаётся за итог всего заказа. */}
      {cheapestHint && (
        <>
          {cheapestHint.isCheapest ? (
            <p className="gaz-sum__hint gaz-sum__hint--best">
              Минимальная стоимость газобетона с доставкой
            </p>
          ) : (
            <div className="gaz-sum__hint gaz-sum__hint--cheaper">
              {/* Что выбрано — одной строкой. Название более дешёвого завода, его
                  сумма и разница дальше стоят на самой кнопке, поэтому текстом они
                  здесь не повторяются. */}
              <p className="gaz-sum__hint-line">
                Выбрано вручную: {formatProductionName(cheapestHint.selected.production)}
              </p>
              {hasUpsells && (
                <p className="gaz-sum__hint-note">Допродажи в сравнении не учтены</p>
              )}
            </div>
          )}

          {/* Три действия одной колонкой и одним классом: рамка, фон, высота и
              внутренние отступы у них совпадают, ширина каждой — по её тексту.
              Порядок постоянный: выгодное производство, требуемое, таблица
              сравнения. Кнопка появляется, только когда ей есть что предложить,
              поэтому дублирующей кнопки уже выбранного или уже показанного завода
              не бывает. Выбор идёт тем же обработчиком, что и клик по строке
              сравнения: пересчитываются материалы, доставка, транспорт, итог и
              готовое сообщение. Сама сводка производство не переключает. */}
          {(cheapestPick || requestedPick || onShowComparison) && (
            <div className="gaz-sum__actions">
              {cheapestPick && (
                <button
                  type="button"
                  className="gaz-sum__pick"
                  onClick={() => onSelectProduction(cheapestPick.comparisonId)}
                >
                  Выбрать {formatProductionName(cheapestPick.production)}
                  {' — '}
                  {formatMoney(cheapestPick.total)} ₽ ·{' '}
                  {/* Зелёным — только сама выгода, как и в подписи «минимальная
                      стоимость»: название завода и общая сумма остаются цветом
                      кнопки. Цифра та же, что и была, — разница из подсказки;
                      своего расчёта здесь нет. Цвет добавляется к слову
                      «экономия», а не заменяет его, и только когда разница
                      положительная: иначе фрагмент остаётся как прежде. */}
                  <span className={cheapestHint.difference > 0 ? 'gaz-sum__pick-saving' : undefined}>
                    экономия {formatMoney(cheapestHint.difference)} ₽
                  </span>
                </button>
              )}
              {/* Прежняя короткая ссылка на искомый завод стала такой же кнопкой
                  и стоит второй: тот же завод, но теперь сразу с суммой и
                  переплатой из его строки сравнения. Переплата показывается,
                  только когда разница действительно положительная. */}
              {requestedPick && (
                <button
                  type="button"
                  className="gaz-sum__pick"
                  onClick={() => onSelectProduction(requestedPick.best.comparisonId)}
                >
                  Выбрать {formatProductionName(requestedPick.best.production)}
                  {' — '}
                  {formatMoney(requestedPick.best.total)} ₽
                  {requestedPick.difference > 0 && ` · дороже на ${formatMoney(requestedPick.difference)} ₽`}
                </button>
              )}
              {onShowComparison && (
                <button type="button" className="gaz-sum__pick" onClick={onShowComparison}>
                  Сравнить все производства
                </button>
              )}
            </div>
          )}
        </>
      )}

    </section>
  );
};

export default GazProposalSummary;
